import {
  Stack,
  Step,
  StepConnector,
  stepConnectorClasses,
  StepIconProps,
  StepLabel,
  Stepper,
  styled,
} from "@mui/material";
import BronzeTierLarge from "@zenith/assets/icons/BronzeTierLarge";
import CheckCircle from "@zenith/assets/icons/CheckCircle";
import Clock from "@zenith/assets/icons/Clock";
import CloseCircle from "@zenith/assets/icons/CloseCircle";
import GoldTierLarge from "@zenith/assets/icons/GoldTierLarge";
import Info from "@zenith/assets/icons/Info";
import PlatinumTierLarge from "@zenith/assets/icons/PlatinumTierLarge";
import SilverTierLarge from "@zenith/assets/icons/SilverTierLarge";
import { useUserDetailsContext } from "@zenith/context/global-context/hooks";
import {
  DEFAULT_CURRENCY_SYMBOL,
  UNLIMITED,
  ZENITH_BANK_TEXT,
  ACCOUNT_NUMBER_LABEL,
} from "@zenith/data/constants";
import {
  UpgradeApproved,
  UpgradeRejected,
  UpgradeWaiting,
} from "@zenith/features/Tiers/assets";
import { AppNavigation } from "@zenith/hooks";
import { DASHBOARD, UPGRADE } from "@zenith/routes/paths";
import {
  ApiResponseDTOFinishUpgradeTierResponseDTO,
  FinishUpgradeTierResponseDTO,
  LimitsInfoDTO,
  StartUpgradeTierRequestDTO,
  TierApiService,
  TierStepsResponseDTO,
  UpgradeTierStepDTO,
} from "@zenith/services/user";
import { Logo } from "@zenith/shared";
import { SpinnerComponent } from "@zenith/components";
import { getSuffixedNumber } from "@zenith/utils/Formaters";
import { getCopyright } from "@zenith/utils/Helpers/getCopyright";
import { memo, useEffect, useState } from "react";

/* eslint-disable-next-line */
export interface EndUpgradePageProps {}

const EndUpgradePage = memo(function EndUpgradePage(
  props: EndUpgradePageProps
) {
  const { navigateTo } = AppNavigation();
  const { userDetailsContext } = useUserDetailsContext();
  const [processStatus, setProcessStatus] = useState<
    FinishUpgradeTierResponseDTO.generalProcessStatus | undefined
  >(undefined);
  const [tierSteps, setTierSteps] = useState<
    Array<UpgradeTierStepDTO> | undefined
  >([]);
  const [accountNumber, setAccountNumber] = useState<string | undefined>(
    undefined
  );
  const [tierType, setTierType] = useState<string | undefined>(undefined);
  const [limitsInfo, setLimitsInfo] = useState<LimitsInfoDTO | undefined>(
    undefined
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const handleDashboardButton = () => {
    navigateTo(DASHBOARD);
  };

  const handleTryAgainButton = async () => {
    try {
      const response = await TierApiService.startUpgradeTier({
        tierType: tierType as StartUpgradeTierRequestDTO.tierType,
      });

      if (response.data) {
        const { tierSteps } = response.data;

        if (tierSteps) {
          const firstRejectedStep = tierSteps.find(
            (step) =>
              step.stepStatus === TierStepsResponseDTO.stepStatus.REJECTED
          );

          const firstNotStartedStep = tierSteps.find(
            (step) =>
              step.stepStatus === TierStepsResponseDTO.stepStatus.NOT_STARTED
          );

          if (firstRejectedStep?.stepNumber) {
            navigateTo(UPGRADE, {
              state: { activeStep: Number(firstRejectedStep.stepNumber) - 1 },
            });
          } else if (firstNotStartedStep?.stepNumber) {
            navigateTo(UPGRADE, {
              state: { activeStep: Number(firstNotStartedStep.stepNumber) - 1 },
            });
          } else {
            navigateTo(UPGRADE, { state: { activeStep: 0 } });
          }
        }
      }
    } catch (error) {
      console.error("Error starting upgrade:", error);
    }
  };

  const getTierColor = (tierType: string | undefined) => {
    switch (tierType) {
      case "SILVER":
        return "#F16613";
      case "GOLD":
        return "#F0B02A";
      case "PLATINUM":
        return "#6EBD7F";
      default:
        return "#000000";
    }
  };

  const tierColor = getTierColor(tierType);

  const getActiveStep = (tierType: string | undefined): number => {
    switch (tierType) {
      case "SILVER":
        return 1;
      case "GOLD":
        return 2;
      case "PLATINUM":
        return 3;
      default:
        return 0;
    }
  };

  const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
      top: 10,
      width: "100%",
      left: "-50px",
    },
    [`&.${stepConnectorClasses.active}`]: {
      [`& .${stepConnectorClasses.line}`]: {
        backgroundColor: getTierColor(tierType),
      },
    },
    [`&.${stepConnectorClasses.completed}`]: {
      [`& .${stepConnectorClasses.line}`]: {
        backgroundColor: getTierColor(tierType),
      },
    },
    [`& .${stepConnectorClasses.line}`]: {
      height: 7,
      border: 0,
      backgroundColor: "#eaeaf0",
      borderRadius: 1,
    },
  }));

  const ColorlibStepIconRoot = styled("div")<{
    ownerState: { completed?: boolean; active?: boolean };
  }>(({ ownerState }) => ({
    backgroundColor: "#DADADB",
    zIndex: 1,
    color: "#fff",
    width: 25,
    height: 25,
    display: "flex",
    borderRadius: "50%",
    borderWidth: "2px",
    justifyContent: "center",
    alignItems: "center",
    ...(ownerState.active && {
      borderColor: getTierColor(tierType),
      width: 35,
      height: 35,
      position: "relative",
      top: "-4px",
    }),
    ...(ownerState.completed && {
      borderColor: getTierColor(tierType),
    }),
  }));

  function stepIcon(props: StepIconProps) {
    const { active, completed, className } = props;

    const icons: { [index: string]: React.ReactElement } = {
      1: <BronzeTierLarge />,
      2: <SilverTierLarge />,
      3: <GoldTierLarge />,
      4: <PlatinumTierLarge />,
    };

    return (
      <ColorlibStepIconRoot
        ownerState={{ completed, active }}
        className={className}
      >
        {icons[String(props.icon)]}
      </ColorlibStepIconRoot>
    );
  }

  const steps = ["BRONZE", "SILVER", "GOLD", "PLATINUM"];

  useEffect(() => {
    TierApiService.finishUpgradeTier()
      .then((response: ApiResponseDTOFinishUpgradeTierResponseDTO) => {
        if ("data" in response && response.data) {
          setIsLoading(false);
          setProcessStatus(response.data.generalProcessStatus);
          setTierSteps(response.data.tierSteps);
          setAccountNumber(response.data.accountNumber);
          setTierType(response.data.tierType);
          setLimitsInfo(response.data.limitsInfo);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        navigateTo("/not-found");
      });
  }, []);

  const getDebitLimit = () => {
    if (tierType === "SILVER") {
      return limitsInfo?.debit?.find((item) => item.type === "PER_TRANSACTION");
    } else {
      return limitsInfo?.debit?.find((item) => item.type === "PER_DAY");
    }
  };

  const getCreditLimit = () => {
    if (tierType === "SILVER") {
      return limitsInfo?.credit?.find(
        (item) => item.type === "PER_TRANSACTION"
      );
    } else {
      return limitsInfo?.credit?.find((item) => item.type === "PER_DAY");
    }
  };

  const getStepStatusIcon = (stepName: string) => {
    if (!tierSteps) return "";

    const foundStep = tierSteps.find((step) => step.stepName === stepName);

    if (!foundStep) return "";
    switch (foundStep?.stepStatus) {
      case UpgradeTierStepDTO.stepStatus.PENDING:
        return <Clock height={17} width={17} />;
      case UpgradeTierStepDTO.stepStatus.REJECTED:
        return <CloseCircle height={17} width={17} />;
      case UpgradeTierStepDTO.stepStatus.APPROVED:
        return <CheckCircle height={17} width={17} />;
      default:
        return "";
    }
  };

  return (
    <div className="mt-8 flex h-[100vh] flex-col items-center">
      {!isLoading ? (
        <>
          <header className="flex justify-center pb-6">
            <Logo />
          </header>
          {processStatus ===
            FinishUpgradeTierResponseDTO.generalProcessStatus.PENDING && (
            <div className="mt-auto min-w-[28%]">
              <div className="mb-8 flex justify-center">
                <UpgradeWaiting />
              </div>
              <div>
                <h1 className="mb-4 text-center text-3.5xl text-neutral-900">
                  Upgrade Under Review
                </h1>
                <p className="text-center text-base text-neutral-600">
                  You can continue your transaction while we process your
                  upgrade request.
                </p>
                <p className="text-center text-base text-neutral-600">
                  We will notify you as soon as it is approved.
                </p>
              </div>
              {tierSteps &&
                tierSteps.some(
                  (step) =>
                    (step.stepName === "PROOF_OF_ADDRESS_STEP" ||
                      step.stepName === "BUSINESS_ADDRESS_STEP") &&
                    step.stepStatus === UpgradeTierStepDTO.stepStatus.PENDING
                ) && (
                  <div className="align-center bg-neutral-info my-8 flex w-full gap-2 rounded-1.25l py-5 pl-5 text-sm">
                    <p className="mt-0.5">{<Info />}</p>
                    <div className="flex flex-col">
                      <p>
                        An agent will be sent to complete physical verification
                      </p>
                      <p>within 24 to 48 hours</p>
                    </div>
                  </div>
                )}
              <div className="mb-8 flex h-0 self-stretch border-t border-neutral-50"></div>
              <div className="ml-2 flex flex-col gap-y-4">
                {tierSteps &&
                  tierSteps.some(
                    (step) => step.stepName === "SELFIE_VERIFICATION_STEP"
                  ) && (
                    <div className="flex flex-row items-center">
                      {getStepStatusIcon("SELFIE_VERIFICATION_STEP")}
                      <div className="ml-2 flex flex-col">
                        <p className="text-sm text-neutral-600">
                          Selfie Verification
                        </p>
                      </div>
                    </div>
                  )}
                {tierSteps &&
                  tierSteps.some(
                    (step) => step.stepName === "NIN_VERIFICATION_STEP"
                  ) && (
                    <div className="flex flex-row items-center">
                      {getStepStatusIcon("NIN_VERIFICATION_STEP")}
                      <div className="ml-2 flex flex-col">
                        <p className="text-sm text-neutral-600">
                          NIN Verification
                        </p>
                      </div>
                    </div>
                  )}
                {tierSteps &&
                  tierSteps.some(
                    (step) => step.stepName === "DOCUMENT_VERIFICATION_STEP"
                  ) && (
                    <div className="flex flex-row items-center">
                      {getStepStatusIcon("DOCUMENT_VERIFICATION_STEP")}
                      <div className="ml-2 flex flex-col">
                        <p className="text-sm text-neutral-600">
                          Document Verification
                        </p>
                      </div>
                    </div>
                  )}
                {tierSteps &&
                  tierSteps.some(
                    (step) => step.stepName === "REGISTRATION_DETAILS_STEP"
                  ) && (
                    <div className="flex flex-row items-center">
                      {getStepStatusIcon("REGISTRATION_DETAILS_STEP")}
                      <div className="ml-2 flex flex-col">
                        <p className="text-sm text-neutral-600">
                          Registration Details
                        </p>
                      </div>
                    </div>
                  )}
                {tierSteps &&
                  tierSteps.some(
                    (step) => step.stepName === "BUSINESS_DETAILS_STEP"
                  ) && (
                    <div className="flex flex-row items-center">
                      {getStepStatusIcon("BUSINESS_DETAILS_STEP")}
                      <div className="ml-2 flex flex-col">
                        <p className="text-sm text-neutral-600">
                          Business Details
                        </p>
                      </div>
                    </div>
                  )}
                {tierSteps &&
                  tierSteps.some(
                    (step) => step.stepName === "BUSINESS_ADDRESS_STEP"
                  ) && (
                    <div className="flex flex-row items-center">
                      {getStepStatusIcon("BUSINESS_ADDRESS_STEP")}
                      <div className="ml-2 flex flex-col">
                        <p className="text-sm text-neutral-600">
                          Business Address
                        </p>
                      </div>
                    </div>
                  )}
                {tierSteps &&
                  tierSteps.some(
                    (step) => step.stepName === "PROOF_OF_ADDRESS_STEP"
                  ) && (
                    <div className="flex flex-row items-center">
                      {getStepStatusIcon("PROOF_OF_ADDRESS_STEP")}
                      <div className="ml-2 flex flex-col">
                        <p className="text-sm text-neutral-600">
                          Proof of Address
                        </p>
                      </div>
                    </div>
                  )}
                {tierSteps &&
                  tierSteps.some(
                    (step) => step.stepName === "DIRECTOR_DETAILS_STEP"
                  ) && (
                    <div className="flex flex-row items-center">
                      {getStepStatusIcon("DIRECTOR_DETAILS_STEP")}
                      <div className="ml-2 flex flex-col">
                        <p className="text-sm text-neutral-600">
                          Director Details
                        </p>
                      </div>
                    </div>
                  )}
              </div>
              <div className="mt-20 flex flex-row justify-center">
                <button
                  id="account-dashboard-button"
                  className="primary-button"
                  onClick={handleDashboardButton}
                >
                  Go to Dashboard
                </button>
              </div>
            </div>
          )}
          {processStatus ===
            FinishUpgradeTierResponseDTO.generalProcessStatus.REJECTED && (
            <div className="mt-auto min-w-[28%]">
              <div className="mb-8 flex justify-center">
                <UpgradeRejected />
              </div>
              <div className="mb-12">
                <h1 className="mb-4 text-center text-3.5xl text-neutral-900">
                  Upgrade Has Been Rejected
                </h1>
                <p className="text-center text-base text-neutral-600">
                  We regret to inform you that the provided documents have been
                </p>
                <p className="text-center text-base text-neutral-600">
                  rejected. Please review and make necessary replacements.
                </p>
              </div>
              <div className="mb-8 flex h-0 self-stretch border-t border-neutral-50"></div>
              <div className="ml-1 flex flex-col gap-y-4">
                {tierSteps &&
                  tierSteps.some(
                    (step) => step.stepName === "SELFIE_VERIFICATION_STEP"
                  ) && (
                    <div className="flex flex-row items-center">
                      {getStepStatusIcon("SELFIE_VERIFICATION_STEP")}
                      <div className="ml-2 flex flex-col">
                        <p className="text-sm text-neutral-600">
                          Selfie Verification
                        </p>
                      </div>
                    </div>
                  )}
                {tierSteps &&
                  tierSteps.some(
                    (step) => step.stepName === "NIN_VERIFICATION_STEP"
                  ) && (
                    <div className="flex flex-row items-center">
                      {getStepStatusIcon("NIN_VERIFICATION_STEP")}
                      <div className="ml-2 flex flex-col">
                        <p className="text-sm text-neutral-600">
                          NIN Verification
                        </p>
                      </div>
                    </div>
                  )}
                {tierSteps &&
                  tierSteps.some(
                    (step) => step.stepName === "DOCUMENT_VERIFICATION_STEP"
                  ) && (
                    <div className="flex flex-row items-center">
                      {getStepStatusIcon("DOCUMENT_VERIFICATION_STEP")}
                      <div className="ml-2 flex flex-col">
                        <p className="text-sm text-neutral-600">
                          Document Verification
                        </p>
                      </div>
                    </div>
                  )}
                {tierSteps &&
                  tierSteps.some(
                    (step) => step.stepName === "REGISTRATION_DETAILS_STEP"
                  ) && (
                    <div className="flex flex-row items-center">
                      {getStepStatusIcon("REGISTRATION_DETAILS_STEP")}
                      <div className="ml-2 flex flex-col">
                        <p className="text-sm text-neutral-600">
                          Registration Details
                        </p>
                      </div>
                    </div>
                  )}
                {tierSteps &&
                  tierSteps.some(
                    (step) => step.stepName === "BUSINESS_DETAILS_STEP"
                  ) && (
                    <div className="flex flex-row items-center">
                      {getStepStatusIcon("BUSINESS_DETAILS_STEP")}
                      <div className="ml-2 flex flex-col">
                        <p className="text-sm text-neutral-600">
                          Business Details
                        </p>
                      </div>
                    </div>
                  )}
                {tierSteps &&
                  tierSteps.some(
                    (step) => step.stepName === "BUSINESS_ADDRESS_STEP"
                  ) && (
                    <div className="flex flex-row items-center">
                      {getStepStatusIcon("BUSINESS_ADDRESS_STEP")}
                      <div className="ml-2 flex flex-col">
                        <p className="text-sm text-neutral-600">
                          Business Address
                        </p>
                      </div>
                    </div>
                  )}
                {tierSteps &&
                  tierSteps.some(
                    (step) => step.stepName === "PROOF_OF_ADDRESS_STEP"
                  ) && (
                    <div className="flex flex-row items-center">
                      {getStepStatusIcon("PROOF_OF_ADDRESS_STEP")}
                      <div className="ml-2 flex flex-col">
                        <p className="text-sm text-neutral-600">
                          Proof of Address
                        </p>
                      </div>
                    </div>
                  )}
                {tierSteps &&
                  tierSteps.some(
                    (step) => step.stepName === "DIRECTOR_DETAILS_STEP"
                  ) && (
                    <div className="flex flex-row items-center">
                      {getStepStatusIcon("DIRECTOR_DETAILS_STEP")}
                      <div className="ml-2 flex flex-col">
                        <p className="text-sm text-neutral-600">
                          Director Details
                        </p>
                      </div>
                    </div>
                  )}
              </div>
              <div className="mt-20 flex flex-row justify-center">
                <button
                  id="go-to-dashboard"
                  className="mr-4 flex
                  h-button-default-height
                  w-large
                  items-center justify-center
                  rounded-lg border-1
                  border-solid border-neutral-300 bg-white px-4
                    py-2 text-center text-sm font-light
                    normal-case text-neutral-600 transition-colors
                    hover:border-white hover:bg-neutral-200
                    active:border-neutral-600 active:bg-neutral-200 disabled:pointer-events-none
                    disabled:border-neutral-100 disabled:bg-white
                    disabled:text-neutral-100"
                  onClick={handleDashboardButton}
                >
                  Go to Dashboard
                </button>
                <button
                  id="try-again-button"
                  className="primary-button"
                  onClick={handleTryAgainButton}
                >
                  Try again
                </button>
              </div>
            </div>
          )}
          {processStatus ===
            FinishUpgradeTierResponseDTO.generalProcessStatus.APPROVED && (
            <div className="relative min-w-[26%]">
              <div className="mt-20 mb-10 flex justify-center">
                <h1 className="text-4.5xl font-medium text-neutral-900">
                  {" "}
                  Thank you {userDetailsContext.firstName}!
                </h1>
              </div>
              <div className="absolute -right-[35px] top-42">
                {" "}
                <UpgradeApproved />{" "}
              </div>
              <div className="h-auto w-[480px] rounded-2xl bg-neutral-200 pb-4">
                <h1 className="w-[62%] pl-6 pt-8 text-3.5xl text-neutral-900">
                  Your account has been upgraded to a
                  <span className="capitalize" style={{ color: tierColor }}>
                    {" "}
                    {tierType?.toLowerCase()} Account
                  </span>
                </h1>
                <div className="mt-10 flex flex-col">
                  <h2 className="pl-6 text-xl capitalize text-neutral-900">
                    {tierType?.toLowerCase()}
                  </h2>
                  <div className="mb-7 flex flex-col pl-6">
                    <div className="flex flex-row">
                      <p className="text-sm text-neutral-400">
                        {ACCOUNT_NUMBER_LABEL}:
                      </p>
                      <div className="ml-1 flex flex-col">
                        <p className="text-neutral-850 text-sm">
                          {accountNumber}
                        </p>
                        <p className="mt-1 text-xs text-neutral-800 sm:text-sm">
                          {ZENITH_BANK_TEXT}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <Stack sx={{ width: "100%" }} spacing={4}>
                      <Stepper
                        alternativeLabel
                        activeStep={getActiveStep(tierType)}
                        connector={<ColorlibConnector />}
                      >
                        {steps.map((tier) => (
                          <Step key={tier}>
                            <StepLabel StepIconComponent={stepIcon}></StepLabel>
                          </Step>
                        ))}
                      </Stepper>
                    </Stack>
                  </div>
                </div>
              </div>
              <div className="flex flex-col justify-center gap-y-2 px-4 py-6">
                <p className="text-neutral-550 text-base">Account Summary</p>
                <div className="my-4 flex h-0 border-t border-neutral-50"></div>
                <div className="flex flex-row justify-between">
                  <p className="text-base text-neutral-600">
                    Credit Limit (
                    {tierType === StartUpgradeTierRequestDTO.tierType.SILVER
                      ? "txn"
                      : "day"}
                    )
                  </p>
                  <p className="text-base text-neutral-900">
                    {getCreditLimit()?.isUnlimited === false
                      ? `${DEFAULT_CURRENCY_SYMBOL} ${getSuffixedNumber(
                          Number(getCreditLimit()?.limit),
                          0,
                          0
                        )}`
                      : UNLIMITED}
                  </p>
                </div>
                <div className="flex flex-row justify-between">
                  <p className="text-base text-neutral-600">
                    Debit Limit (
                    {tierType === StartUpgradeTierRequestDTO.tierType.SILVER
                      ? "txn"
                      : "day"}
                    )
                  </p>
                  <p className="text-base text-neutral-900">
                    {getDebitLimit()?.isUnlimited === false
                      ? `${DEFAULT_CURRENCY_SYMBOL} ${getSuffixedNumber(
                          Number(getDebitLimit()?.limit),
                          0,
                          0
                        )}`
                      : UNLIMITED}
                    {getDebitLimit()?.isUnlimited === false &&
                    getDebitLimit()?.has2faLimit === true
                      ? `${" /"} ${getSuffixedNumber(
                          Number(getDebitLimit()?.limit2FA),
                          0,
                          0
                        )} ${" (2FA)"}`
                      : ""}
                  </p>
                </div>
                <div className="flex flex-row justify-between">
                  <p className="text-base text-neutral-600">Balance Limit</p>
                  <p className="text-base text-neutral-900">
                    {limitsInfo?.balance?.isUnlimited === false
                      ? `${DEFAULT_CURRENCY_SYMBOL} ${getSuffixedNumber(
                          Number(limitsInfo?.balance?.limit),
                          0,
                          0
                        )}`
                      : UNLIMITED}
                  </p>
                </div>
              </div>
              <div className="mt-8 flex flex-row justify-center">
                <button
                  id="account-dashboard-button"
                  className="primary-button"
                  onClick={handleDashboardButton}
                >
                  Go to Dashboard
                </button>
              </div>
            </div>
          )}
          <span className="mt-auto mb-12 flex justify-center text-sm font-normal text-neutral-400">
            {getCopyright()}
          </span>
        </>
      ) : (
        <div className="flex h-full w-full items-center justify-center">
          <SpinnerComponent type="primary" size={100} showIcon />
        </div>
      )}
    </div>
  );
});

export default EndUpgradePage;
